// Pathname-to-tool routing utilities shared by Sidebar and HelpPanel.
//
// toolHrefFor is the single source of truth for every tool's URL: navigation
// and active-state detection both use it so they can never disagree.

import { COCKPIT_DEPARTMENTS } from '@/data/tools.config';

export function toolHrefFor(deptId: string, toolId: string): string {
  if (deptId === 'administration' && toolId === 'access') return '/admin/access';
  if (deptId === 'connect') {
    if (toolId === 'connect-partners') return '/connect';
    if (toolId === 'connect-pricing') return '/connect/pricing';
    if (toolId === 'connect-agreements') return '/connect/agreements';
    if (toolId === 'connect-adoption') return '/connect/adoption';
  }
  if (deptId === 'stores') {
    if (toolId === 'stores-today') return '/stores/today';
    if (toolId === 'stores-thread') return '/stores/thread';
    if (toolId === 'stores-chat') return '/stores/chat';
    if (toolId === 'stores-content') return '/stores/content';
    if (toolId === 'stores-inventory') return '/stores/inventory';
    if (toolId === 'inventory-imports') return '/stores/hq/imports';
    if (toolId === 'stores-admin-users') return '/stores/users';
    if (toolId === 'stores-admin-tablets') return '/stores/tablets';
    if (toolId === 'stores-admin-checklist') return '/stores/hq/checklist';
    if (toolId === 'stores-info') return '/stores/setup/info';
    if (toolId === 'stores-paperwork') return '/stores/setup/paperwork';
    if (toolId === 'stores-hq') return '/stores/hq';
    if (toolId === 'stores-hq-questions') return '/stores/hq/questions';
    if (toolId === 'stores-hq-pulse') return '/stores/hq/pulse';
    if (toolId === 'stores-hq-broadcast') return '/stores/hq/broadcast';
    if (toolId === 'stores-hq-photos') return '/stores/hq/photos';
    if (toolId === 'allocation') return '/retail/allocation';
  }
  // Reports kept the routes they had under Stores, so bookmarks still work.
  if (deptId === 'reports') {
    if (toolId === 'fleet-map') return '/retail/fleet-map';
    if (toolId === 'performance') return '/retail/performance';
    if (toolId === 'leakage') return '/retail/leakage';
    if (toolId === 'item-sales') return '/retail/item-sales';
    if (toolId === 'stores-hq-variance') return '/stores/hq/inventory';
    if (toolId === 'stores-door-times') return '/stores/door-times';
    if (toolId === 'stores-employee-purchases') return '/stores/employee-purchases';
    if (toolId === 'unposted-activity') return '/reports/unposted-activity';
    if (toolId === 'loyalty') return '/reports/loyalty';
    if (toolId === 'store-analytics') return '/reports/store-analytics';
    if (toolId === 'on-order') return '/reports/on-order';
    if (toolId === 'fleet-hourly') return '/reports/fleet-hourly';
    if (toolId === 'cash-credit') return '/reports/cash-credit';
    if (toolId === 'trailing-12') return '/reports/trailing-12';
    if (toolId === 'sales-tax') return '/reports/sales-tax';
    if (toolId === 'gift-cards') return '/reports/gift-cards';
    if (toolId === 'gl-exports') return '/reports/gl-exports';
    // A shortcut card: Commerce > Inventory's own page.
    if (toolId === 'commerce-inventory') return '/commerce/inventory';
    // 'inventory-exports' is the pre-rename id: a pinned tool saved under it still opens the right page.
    if (toolId === 'item-file' || toolId === 'inventory-exports') return '/reports/item-file';
  }
  if (deptId === 'comms') {
    if (toolId === 'comms-dashboard') return '/comms/dashboard';
    if (toolId === 'comms-calllog') return '/comms/call-log';
  }
  if (deptId === 'it' && toolId === 'notification-log') return '/it/notifications';
  return `/${deptId}/${toolId}`;
}

function pathOnTool(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function activeToolForPath(
  pathname: string,
): { deptId: string; toolId: string } | null {
  let best: { deptId: string; toolId: string; href: string } | null = null;
  for (const dept of COCKPIT_DEPARTMENTS) {
    for (const tool of dept.tools) {
      const href = toolHrefFor(dept.id, tool.id);
      if (!pathOnTool(pathname, href)) continue;
      if (!best || href.length > best.href.length) {
        best = { deptId: dept.id, toolId: tool.id, href };
      }
    }
  }
  return best ? { deptId: best.deptId, toolId: best.toolId } : null;
}
